// src/components/Hero.js
import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';

/* ===== timings (ms) ===== */
const AUTOPLAY = 4000;
const FADE_MS  = 1800;
const LAST_MAX = 4500;
const FADE_S   = `${FADE_MS / 1000}s`;

/* ===== Styles ===== */
const HeroSection = styled.section`
  position: relative;
  inset: 0;
  width: 100%;
  height: 100dvh;
  @supports not (height: 100dvh) { height: 100svh; }
  @supports not (height: 100svh) { height: 100vh; }
  z-index: 1;
  overflow: hidden;
  background: #000;

  /* 스와이프 제스처: 세로 스크롤은 허용, 가로는 우리가 처리 */
  touch-action: pan-y;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;

  @media (max-width: 1024px) and (orientation: landscape) {
    min-height: 640px;
  }
`;

const HeroVideo = styled.video`
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover;
  object-position: center center;
  transition: opacity ${FADE_S} ease-in-out;
  opacity: ${p => (p.active && !p.ending ? 1 : 0)};
`;

const Overlay = styled.div`
  position: absolute; inset: 0;
  background:
      linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 35%, rgba(0,0,0,0.35) 100%);
  z-index: 2;
  pointer-events: none;

  @media (max-width: 768px) {
    background:
        linear-gradient(to bottom, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.30) 40%, rgba(0,0,0,0.45) 100%);
  }
`;

const Indicators = styled.div`
  position: absolute;
  bottom: clamp(16px, 4.5vh, 36px);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: clamp(6px, 1.2vw, 12px);
  z-index: 4;
`;

const Dot = styled.button`
  width: clamp(8px, 1.2vw, 12px);
  height: clamp(8px, 1.2vw, 12px);
  border-radius: 50%;
  border: none;
  cursor: pointer;
  background: ${p => (p.active ? '#fff' : 'rgba(255,255,255,.52)')};
  transition: background .2s, transform .08s ease;
  &:hover { background: #fff; }
  &:active { transform: scale(0.92); }
`;

const floatUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const CaptionWrap = styled.div`
  position: absolute; inset: 0;
  display: grid; place-items: center;
  z-index: 3;
  pointer-events: none;
  padding: 0 clamp(16px, 6vw, 96px);
`;

const CaptionInner = styled.div`
  max-width: min(1100px, 92vw);
  text-align: center; color: #fff;

  ${p => p.animate && css` & h1 { animation: ${floatUp} 700ms ease-out both; } `}
  transition: opacity ${FADE_S} ease, transform ${FADE_S} ease;
  opacity: ${p => (p.ending ? 0 : 1)};
  transform: ${p => (p.ending ? 'translateY(-8px)' : 'none')};
  ${p => p.ending && css` & h1 { animation: none !important; } `}

  @media (max-width: 768px) { & h1 { line-height: 1.25; } }
`;

const Title = styled.h1`
  font-size: clamp(28px, 7vw, 84px);
  font-weight: 800;
  line-height: 1.35;
  letter-spacing: -0.02em;
  margin: 0 0 clamp(10px, 2vh, 18px);
  white-space: pre-line;
  text-shadow:
      0 1px 2px rgba(0,0,0,.35),
      0 2px 14px rgba(0,0,0,.35);
  @media (max-width: 480px) { letter-spacing: -0.01em; }
`;

/* ====== Component ====== */
const Hero = () => {
  const slides = [
    { url: '/videos/video1.mp4', title: '변화하는 도로,\n안전으로 답하다' },
    { url: '/videos/video3.mp4', title: '국민의 안전을 최우선으로' },
    { url: '/videos/video2.mp4', title: '도로안전의 미래를 설계합니다' },
  ];

  const [index, setIndex] = useState(0);
  const [ending, setEnding] = useState(false);
  const videoRefs = useRef([]);
  const timerRef  = useRef(null);
  const lastCapRef = useRef(null);
  const endedHandlerRef = useRef(null);
  const didEndOnceRef = useRef(false);

  /* swipe refs */
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const draggingRef = useRef(false);   // 마우스 드래그용
  const swipingRef = useRef(false);    // 가로 스와이프 인식중 여부

  const clearTimer = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  };
  const clearLastCap = () => {
    if (lastCapRef.current) { clearTimeout(lastCapRef.current); lastCapRef.current = null; }
  };
  const detachLastEnded = () => {
    const lastIdx = slides.length - 1;
    const v = videoRefs.current[lastIdx];
    if (v && endedHandlerRef.current) {
      v.removeEventListener('ended', endedHandlerRef.current);
      endedHandlerRef.current = null;
    }
  };

  /* 타이머/리스너를 현재 index 기준으로 세팅 */
  const scheduleForIndex = (idx) => {
    clearTimer();
    clearLastCap();
    detachLastEnded();
    setEnding(false);
    didEndOnceRef.current = false;

    const isLast = idx === slides.length - 1;
    const cur = videoRefs.current[idx];
    if (cur) {
      cur.currentTime = cur.currentTime || 0;
      cur.play().catch(()=>{});
    }

    if (!isLast) {
      timerRef.current = setTimeout(() => goto((idx + 1) % slides.length, { fromUser: false }), AUTOPLAY);
    } else {
      const onEnd = () => {
        if (didEndOnceRef.current) return;
        didEndOnceRef.current = true;
        clearLastCap();
        setEnding(true);
        setTimeout(() => goto(0, { fromEnded: true }), FADE_MS);
      };
      endedHandlerRef.current = onEnd;
      cur?.addEventListener('ended', onEnd);
      lastCapRef.current = setTimeout(onEnd, LAST_MAX);
    }
  };

  /* 초기 로드 & 첫 재생 */
  useEffect(() => {
    videoRefs.current.forEach(v => v?.load());
    const t = setTimeout(() => videoRefs.current[0]?.play().catch(()=>{}), 400);
    return () => clearTimeout(t);
  }, []);

  /* 인덱스 변경 시 스케줄링 */
  useEffect(() => {
    scheduleForIndex(index);
    return () => {
      clearTimer();
      clearLastCap();
      detachLastEnded();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  const goto = (next, opts = {}) => {
    const { fromEnded = false } = opts;
    if (next === index) { // 같은 인덱스로 재호출이면 현 상태 유지
      scheduleForIndex(index);
      return;
    }
    clearTimer();
    clearLastCap();
    detachLastEnded();
    setEnding(false);

    const nv = videoRefs.current[next];
    if (nv) {
      nv.currentTime = 0;
      nv.play().catch(()=>{});
    }
    setIndex(next);

    // 전환 후, 다른 비디오는 잠깐 있다가 일시정지
    setTimeout(() => {
      videoRefs.current.forEach((v, i) => { if (i !== next) v?.pause(); });
    }, fromEnded ? 0 : FADE_MS);
  };

  /* ---- Swipe handlers (모바일 터치 + 데스크탑 드래그) ---- */
  const SWIPE_MIN_PX = 60;

  const onStart = (x, y) => {
    startXRef.current = x;
    startYRef.current = y;
    swipingRef.current = false;
    draggingRef.current = true;
    // 제스처 중에는 자동 전환 잠시 중지
    clearTimer();
    clearLastCap();
    detachLastEnded();
  };

  const onMove = (x, y, e) => {
    if (!draggingRef.current) return;
    const dx = x - startXRef.current;
    const dy = y - startYRef.current;

    // 가로 스와이프만 인식 (세로 스크롤 방해 최소화)
    if (!swipingRef.current && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
      swipingRef.current = true;
    }
    if (swipingRef.current) {
      e?.preventDefault?.();
    }
  };

  const onEnd = (xEnd) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;

    const dx = xEnd - startXRef.current;
    const threshold = Math.max(SWIPE_MIN_PX, (window.innerWidth || 360) * 0.1);

    if (swipingRef.current && Math.abs(dx) >= threshold) {
      if (dx < 0) goto((index + 1) % slides.length, { fromUser: true });
      else goto(index === 0 ? slides.length - 1 : index - 1, { fromUser: true });
    } else {
      // 스와이프가 아니면 현재 인덱스 기준으로 타이머만 재시작
      scheduleForIndex(index);
    }
    swipingRef.current = false;
  };

  return (
      <HeroSection
          id="home"
          onTouchStart={(e) => onStart(e.touches[0].clientX, e.touches[0].clientY)}
          onTouchMove={(e) => onMove(e.touches[0].clientX, e.touches[0].clientY, e)}
          onTouchEnd={(e) => onEnd((e.changedTouches?.[0]?.clientX) ?? startXRef.current)}
          onMouseDown={(e) => onStart(e.clientX, e.clientY)}
          onMouseMove={(e) => onMove(e.clientX, e.clientY, e)}
          onMouseUp={(e) => onEnd(e.clientX)}
          onMouseLeave={(e) => draggingRef.current && onEnd(e.clientX)}
      >
        {slides.map((s, i) => (
            <HeroVideo
                key={i}
                ref={el => (videoRefs.current[i] = el)}
                active={i === index}
                ending={ending && i === index}
                muted
                playsInline
                preload="auto"
            >
              <source src={s.url} type="video/mp4" />
            </HeroVideo>
        ))}

        <Overlay />

        <CaptionWrap>
          <CaptionInner key={index} animate ending={ending}>
            <Title>{slides[index].title}</Title>
          </CaptionInner>
        </CaptionWrap>

        <Indicators>
          {slides.map((_, i) => (
              <Dot
                  key={i}
                  active={i === index}
                  onClick={() => goto(i, { fromUser: true })}
                  aria-label={`${i+1}번 슬라이드`}
              />
          ))}
        </Indicators>
      </HeroSection>
  );
};

export default Hero;
